import axios from 'axios';

// Default to the Solara Render backend. Previously this only read from env,
// and the dev `.env` pointed at the old Emergent backend — which won CRA's
// precedence and made the deployed app talk to the wrong (and now-sleeping)
// server. The hardcoded default makes the live app always reach Render.
// Production builds should set REACT_APP_BACKEND_URL=https://api-cashflow.sgcap.au
// (the onrender.com URL below is the fallback, not the canonical endpoint).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://solara-cashflow-api.onrender.com';
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('flux_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors.
// Skip the redirect for login/register failures (a wrong password must show
// its error, not reload the page) and when we're already on /login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthAttempt && window.location.pathname !== '/login') {
      localStorage.removeItem('flux_token');
      localStorage.removeItem('flux_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// User API
export const userAPI = {
  updateProfile: (data) => api.put('/users/profile', data),
};

// Cashflow API
export const cashflowAPI = {
  getAll: () => api.get('/cashflows'),
  create: (data) => api.post('/cashflows', data),
  update: (id, data) => api.put(`/cashflows/${id}`, data),
  delete: (id) => api.delete(`/cashflows/${id}`),
  getMembers: (id) => api.get(`/cashflows/${id}/members`),
  addMember: (id, email) => api.post(`/cashflows/${id}/members`, { email }),
  removeMember: (cashflowId, memberId) => api.delete(`/cashflows/${cashflowId}/members/${memberId}`),
};

// Category API
export const categoryAPI = {
  getAll: (cashflowId) => api.get(`/categories/${cashflowId}`),
  create: (data) => api.post('/categories', data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Expense API
export const expenseAPI = {
  getAll: (cashflowId) => api.get(`/expenses/${cashflowId}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  markPaid: (id, data) => api.post(`/expenses/${id}/pay`, data),
  unmarkPaid: (id, occurrenceDate) =>
    api.delete(`/expenses/${id}/pay`, { params: occurrenceDate ? { occurrence_date: occurrenceDate } : {} }),
  toggle: (id) => api.put(`/expenses/${id}/toggle`),
};

// Dashboard state API (running balances / payment marks / weekly payments — per cashflow)
export const dashboardAPI = {
  getState: (cashflowId) => api.get(`/cashflows/${cashflowId}/dashboard-state`),
  saveState: (cashflowId, state) => api.put(`/cashflows/${cashflowId}/dashboard-state`, state),
};

// Balance-ledger API (per-expense running balance: opening / accrual / payment / adjustment)
export const balanceAPI = {
  getBalances: (cashflowId) => api.get(`/cashflows/${cashflowId}/balances`),
  addEntry: (cashflowId, expenseId, data) =>
    api.post(`/cashflows/${cashflowId}/expenses/${expenseId}/balance-entry`, data),
  closeMonths: (cashflowId) => api.post(`/cashflows/${cashflowId}/close-months`),
};

// Forecast API
export const forecastAPI = {
  get: (cashflowId, months = 24, expenseType = null) => {
    const params = new URLSearchParams({ months: months.toString() });
    if (expenseType) params.append('expense_type', expenseType);
    return api.get(`/forecast/${cashflowId}?${params.toString()}`);
  },
};

export default api;
